import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  CoursesApi,
  editFormDataAF,
  EditFormMode,
  ICourseDto,
  Log,
  normalizeCourseMetadata,
  simpleResultErrorEmptyResponse,
  useDrawers,
  useTimezone,
  warnAndToastException,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { useStationsBulkSelection } from "../stations";
import { useCoursesByIdsNavigation } from "./use-courses-by-ids-navigation";

/** The draft seeded here and the drawer opened for it must agree on this one form mode. */
const COURSE_DRAFT_FORM_MODE = EditFormMode.UnboundDrawer;

/**
 * Bulk "Create course" on the stations page: turns the current station selection into a pre-filled course
 * draft, opens the course drawer over the stations page, and — once saved — offers a jump to the new course
 * on the courses page.
 */
export const useCreateCourseFromStations = () => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { openCourseEditFormDrawer } = useDrawers();
  const { navigateToCoursesByIds } = useCoursesByIdsNavigation();
  const { removeStationsBulkSelectedIds } = useStationsBulkSelection();
  const setCourseDto = useSetRecoilState<Partial<ICourseDto> | undefined>(
    editFormDataAF(["course", COURSE_DRAFT_FORM_MODE]),
  );

  const createCourseFromStations = useEvent(
    async (stationIds: number[]): Promise<void> => {
      if (!stationIds?.length) {
        return;
      }

      let draft: Partial<ICourseDto> | undefined;

      try {
        const draftResult = await CoursesApi.buildDraftFromStations(stationIds);

        if (!draftResult?.success || !draftResult.data) {
          warnAndToastSimpleResult(
            draftResult ?? simpleResultErrorEmptyResponse(t),
            t,
          );
          return;
        }

        draft = draftResult.data;
      } catch (error) {
        // Surfaces the server's own translated reason; the station key is only the fallback.
        warnAndToastException({
          error,
          fallbackTranslationKey: "station.CREATE_COURSE_FAILED",
          translationFunction: t,
        });
        return;
      }

      Log.debug("Course draft composed from selected stations:", draft);

      // Only fields that should reach the create endpoint belong here: the drawer sends the whole form
      // snapshot for a pre-filled draft, so a generated column like `createdAt` must not be seeded.
      setCourseDto({
        ...draft,
        // `innerAreaIds` is derived on the client only, so the server draft has to pass through here too.
        metadata: normalizeCourseMetadata({
          metadata: draft.metadata || {},
          reorderStationsByTime: false,
          timezone,
        }),
      });

      const drawerResult = await openCourseEditFormDrawer({
        mode: COURSE_DRAFT_FORM_MODE,
        hasPrefilledDraft: true,
      });
      const savedCourseId = drawerResult?.data?.id;

      if (!drawerResult?.success || !savedCourseId) {
        return;
      }

      // Only the stations that went into the course are released; any other pick stays selected.
      removeStationsBulkSelectedIds(stationIds);

      Log.successAndToastAction({
        message: t("course.CREATED_FROM_STATIONS_REVIEW"),
        actionLabel: t("course.CREATED_FROM_STATIONS_LINK"),
        onAction: () => navigateToCoursesByIds([savedCourseId]),
      });
    },
  );

  return { createCourseFromStations };
};
